import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';

import { observer } from 'mobx-react';
import { mergeStyleSets, FontSizes, FontWeights } from '@fluentui/react';

import Separator from 'src/components/base/Separator';
import { CheckboxSetting } from './common';


const classNames = mergeStyleSets({
  root: {
    // paddingBottom: '20px',
    margin: 12,
    height: '100%',
  },

  settingsContainer: {
    selectors: {
      '&> * + *': {
        // marginTop: 20,
      }
    },
  },
});


const Const = {
  settings: [
    { id: 'EnforceNewOwnerHasReadAccess',
      title: 'Enforce New Owner Has Read Access',
      text: `If true, the record's new owner must have at least read access on the record.`, 
      default: false,
    },
    { id: 'KeepAccountTeam',
      title: 'Keep Account Team',
      text: 'If true, the account team is kept with the account when the account owner is changed. If false, the account team is deleted. Default is false. This action only applies to team members added by a Salesforce admin, the account owner, or someone higher in the role hierarchy. Team members added by users with group-based access are removed even if true.',
      default: false,
    },
    { id: 'KeepSalesTeam',
      title: 'Keep Sales Team',
      text: 'If true, the opportunity team is kept with the opportunity when the account owner is changed. If false, the opportunity team is deleted. Default is false.',
      default: false,
    },
    { id: 'KeepSalesTeamGrantCurrentOwnerReadWriteAccess',
      title: 'Keep Sales Team Grant Current Owner Read Write Access',
      text: `If true, the opportunity's previous owner retains read/write access after the owner is changed. Default is false. Can be true only when KeepSalesTeam is true.`,
      default: false,
    },
    { id: 'SendEmail',
      title: 'Send Email',
      text: 'If true, an email notification is sent to the new owner. Default is false.',
      default: false,
    },
    { id: 'TransferAllOwnedCases',
      title: 'Transfer All Owned Cases',
      text: 'If true, all cases (open and closed) owned by the account owner are transferred to the new owner. Default is false. When TransferAllOwnedCases is true, TransferOwnedOpenCases must also be true.',
      default: false,
    },
    { id: 'TransferArticleOwnedPublishedVersion',
      title: 'Transfer Article Owned Published Version',
      text: `If true and the record is a Knowledge article, the article owner's published version for the language of the current draft is transferred to the new owner, in addition to the current draft.`,
      default: false,
    },
    { id: `TransferArticleOwnedArchivedVersions`,
      title: `Transfer Article Owned Archived Versions`,
      text: `If true and the record is a Knowledge article, the article owner's archived versions for the language of the current draft are transferred to the new owner, in addition to the current draft.`,
      default: false,
    },
    { id: `TransferArticleAllVersions`,
      title: `Transfer Article All Versions`,
      text: `If true and the record is a Knowledge article, all published and archived versions owned by anyone for the language of the current draft are transferred to the new owner, in addition to the current draft.`,
      default: false,
    },
    { id: `TransferContacts`,
      title: `Transfer Contacts`,
      text: `If true and the record is a business account, contacts associated with the account are transferred to the new owner.`,
      default: false,
    },
    { id: `TransferContracts`,
      title: `Transfer Contracts`,
      text: `If true and the record is an account, contracts associated with the account and owned by the account owner are transferred to the new owner.`,
      default: false,
    },
    { id: `TransferNotesAndAttachments`,
      title: `Transfer Notes And Attachments`,
      text: `If true, the record's notes, attachments, and Google Docs are transferred to the new record owner. If false, the original record owner retains ownership.`,
      default: false,
    },
    { id: `TransferOpenActivities`,
      title: `Transfer Open Activities`,
      text: `If true, the record's open activities are transferred to the new owner.`, 
      default: false,
    },
    { id: `TransferOrders`,
      title: `Transfer Orders`,
      text: `If true and the record is an account, the draft standalone orders associated with the account and draft orders associated with transferred contracts owned by the account owner are transferred to the new owner.`,
      default: false,
    },
    { id: `TransferOthersOpenOpportunities`,
      title: `Transfer Others Open Opportunities`,
      text: `If true and the record is an account, open opportunities associated with the account and not owned by the current owner are transferred to the new owner. When this option is executed, TransferOwnedOpenOpportunities must be set to execute. Default is false.`,
      default: false,
    },
    { id: `TransferOwnedClosedOpportunities`,
      title: `Transfer Owned Closed Opportunities`,
      text: `If true and the record is an account, closed opportunities owned by the account owner are transferred to the new owner. Default is false.`,
      default: false,
    },
    { id: `TransferOwnedOpenCases`,
      title: `Transfer Owned Open Cases`,
      text: `If true and the record is an account, open cases owned by the account owner are transferred to the new owner. Default is false.`,
      default: false,
    },
    { id: `TransferOwnedOpenOpportunities`,
      title: `Transfer Owned Open Opportunities`,
      text: `If true and the record is an account, open opportunities associated with the account and owned by the account owner are transferred to the new owner.`,
      default: false,
    },
  ].map(x => ({ ...x, selected: false })),
};


export const OwnerChangeOptions = observer(({ mappingManager, pageControl, onChange }) => {
  const { disabled, mapping } = mappingManager;
  const { ownerChangeOptions = [] } = mapping;

  const [settings, setSettings] = useState(Const.settings);
  const [isDirty, setDirty] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const commands = disabled
      ? []
      : [
        { id: 'reset', iconProps: { iconName: 'Clear' }, onClick: handleResetSettings },
        { id: 'apply',
          iconProps: { iconName: 'Accept' },
          onClick: handleSaveSettings
        },
      ];
    pageControl.setHeaderCommands(commands);
  }, [disabled, settings]);

  useEffect(() => {
    pageControl.setDirtyActions(isDirty
      ? { 
        text: 'Settings changed', 
        onSave: async () => handleSaveSettings(), 
        onForget: async () => handleForgetChanges(),
      }
      : null);
  }, [isDirty]);


  function loadSettings() {
    const s = Const.settings.map(x => {
      const v = ownerChangeOptions.find(opt => opt.id === x.id);
      return { ...x, selected: v ? v.value : x.default };
    });
    setSettings(s);
    setDirty(false);
  }

  const handleSettingChange = useCallback((newItem) => {
    setSettings(prevSettings => prevSettings.map(x => x.id === newItem.id ? newItem : x));
    setDirty(true);
  }, [mappingManager]);

  function handleSaveSettings() {
    const c = settings.map(x => ({ id: x.id, value: x.selected }));
    mappingManager.saveOwnerChangeOptions(c);
    onChange(c);
  }

  function handleForgetChanges() {
  }

  function handleResetSettings() {
    loadSettings();
  }

  return (
    <div className={classNames.root}>
      <div className={classNames.settingsContainer}>
        {settings.map((x, index) => 
        <>
          <OwnerChangeOption
            key={x.id}
            setting={x}
            disabled={disabled}
            onChange={handleSettingChange}
          />
          <Separator styles={{ root: { marginTop: 0 }} }/>
        </>
          )}
      </div>
    </div>
  );
});


function OwnerChangeOption({ setting, disabled, onChange }) {
  const { selected, text, title } = setting;
  
  function handleChecked(event, checked) {
    onChange({ ...setting, selected: checked });
  }

  return (
    <CheckboxSetting 
      selected={selected}
      title={title}
      text={text}
      onChange={handleChecked}
      disabled={disabled}
    />
  );
}


export default OwnerChangeOptions;